import { mapboxToken } from "@/constants";
import { BAM_MAP_STYLE_SOURCES } from "@/constants/mapStyles";
import { useUpdateLeaseCompFieldRuleMutation } from "@/services/leaseCompFieldRules";
import { useFetchGeocodeAddressMutation } from "@/services/survey-core";
import { checkValueIsUnavailable } from "@/shared/utils";
import * as mapUtil from "@/shared/utils/map";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import type {
  LeaseComp,
  LeaseCompFields,
} from "@survey-types/survey.leaseComp.types";
import classNames from "classnames";
import debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import MapOverlay from "../properties/MapOverlay";
import EditableLeaseCompField from "./EditableLeaseCompField";
import EditLeaseCompFieldLabel from "./EditLeaseCompFieldLabel";

import type { ReactNode } from "react";

interface LeaseCompDetailAddressFormProps {
  leaseComp: LeaseComp;
  leaseCompId: string;
  fieldRules?: LeaseCompFieldRule[];
  onUpdate: (id: string, updates: Partial<LeaseComp>) => void;
  photoSection?: ReactNode;
}

const LeaseCompDetailAddressForm = ({
  leaseComp,
  leaseCompId,
  fieldRules,
  onUpdate,
  photoSection,
}: LeaseCompDetailAddressFormProps) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapAddressContainerRef = useRef<HTMLDivElement | null>(null);
  const isMapInitialized = useRef<boolean>(false);

  const latitude = leaseComp?.leaseCompFields?.latitude;
  const longitude = leaseComp?.leaseCompFields?.longitude;

  const defaultMapValues = {
    mapCentroid: mapUtil.isValidCoordinates([
      parseFloat(longitude ?? ""),
      parseFloat(latitude ?? ""),
    ])
      ? [parseFloat(longitude ?? ""), parseFloat(latitude ?? "")]
      : [-95, 40],
    mapZoomLevel: 12,
  };

  const [coordinates, setCoordinates] = useState<[any, any]>([
    longitude,
    latitude,
  ]);
  const [mapData, setMapData] = useState<any>(defaultMapValues);
  const [isLatInvalid, setIsLatInvalid] = useState(false);
  const [isLngInvalid, setIsLngInvalid] = useState(false);
  const [addressDetails, setAddressDetails] = useState(leaseComp);
  const [fetchGeocodeAddress] = useFetchGeocodeAddressMutation();
  const [updateLeaseCompFieldRule] = useUpdateLeaseCompFieldRuleMutation();
  const [editingFieldRule, setEditingFieldRule] =
    useState<LeaseCompFieldRule | null>(null);

  // Helper function to fetch coordinates via geocoding API
  const fetchCoords = (payload: any) => {
    return fetchGeocodeAddress(JSON.stringify(payload))
      .then((response) => {
        return response?.data?.features[0]?.geometry?.coordinates;
      })
      .catch((_error) => {
        console.error("Error fetching coordinates");
        return null;
      });
  };

  // Helper function to normalize dash values to empty string
  const normalizeValue = (value: string | undefined) => {
    return value === "—" ? "" : value;
  };

  const updateAddress = (value: string, field: keyof LeaseCompFields) => {
    // If the user focused a "—" field without entering a value, normalizeValue
    // turned the display into "" but the stored value is still "—". Skip the
    // PATCH so we don't overwrite "—" with an empty string unintentionally.
    if (
      value.trim() === "" &&
      leaseComp.leaseCompFields?.[field] === "\u2014"
    ) {
      return;
    }

    if (value !== leaseComp.leaseCompFields?.[field]) {
      // Update nested leaseCompFields
      onUpdate(leaseCompId, {
        leaseCompFields: {
          [field]: value,
        },
      });
    }
  };

  const updateMapView = (newCenter: [number, number], newZoom: number) => {
    const map = mapRef.current;
    map?.setCenter(newCenter);
    map?.setZoom(newZoom || 2.5);
  };

  const isMapReady = () => {
    if (!mapRef.current || !isMapInitialized.current) return false;
    try {
      return Boolean(mapRef.current.getContainer());
    } catch {
      return false;
    }
  };

  const renderMapPoints = (coordinatesParam: [number, number]) => {
    if (!isMapReady()) return;
    const map = mapRef.current;
    if (!map) return;

    mapUtil.deleteMarkers(map);

    // Create a pseudo-property object for the map utilities
    const pseudoProperty = {
      latitude: coordinatesParam[1],
      longitude: coordinatesParam[0],
      propertyName: leaseComp?.leaseCompFields?.propertyName || "",
    };

    const points = mapUtil.getPointsForAddressForm(
      [coordinatesParam[0], coordinatesParam[1]],
      pseudoProperty as any,
    );

    if (mapUtil.isValidCoordinates(coordinatesParam)) {
      mapUtil.getMarkerConfig(points, map);
      updateMapView(coordinatesParam, 12);
      mapEvent();
    } else {
      updateMapView([-95, 40], 2.5);
    }
  };

  const mapEvent = () => {
    const map = mapRef.current;
    if (!map) return;

    const handleZoom = (e: { originalEvent?: Event }) => {
      if (e && !e.originalEvent) return;
      const data = { ...mapData, mapZoomLevel: map.getZoom() };
      setMapData(data);
    };

    const handleCentroid = () => {
      const center = map.getCenter();
      const data = {
        ...mapData,
        mapCentroid: [center.lng, center.lat],
      };
      setMapData(data);
    };

    const { _markers: markers } = mapRef.current as any;
    if (markers?.length > 0) {
      markers.forEach((marker: mapboxgl.Marker) => {
        const handleDragEnd = () => {
          const geoDetail = marker.getLngLat();
          const { lng, lat } = geoDetail;
          if (lng && lat) {
            setCoordinates([lng, lat]);
            onUpdate(leaseCompId, {
              leaseCompFields: {
                ...leaseComp.leaseCompFields,
                latitude: `${lat}`,
                longitude: `${lng}`,
              },
            });
          }
        };

        marker.on("dragend", handleDragEnd);
      });
    }

    map.on("zoomend", debounce(handleZoom) as any);
    map.on("dragend", debounce(handleCentroid) as any);
  };

  // Re-initialise local state only when the user switches to a different lease comp.
  // Using leaseCompId (not the whole leaseComp object) prevents API save responses
  // from overwriting the form while the user is still typing.
  useEffect(() => {
    if (leaseComp) {
      // Reset address details to current lease comp
      setAddressDetails(leaseComp);
      
      // Reset coordinates
      const newLatitude = leaseComp?.leaseCompFields?.latitude;
      const newLongitude = leaseComp?.leaseCompFields?.longitude;
      const newCoordinates: [any, any] = [newLongitude, newLatitude];
      setCoordinates(newCoordinates);
      
      // Reset map data
      const newMapData = {
        mapCentroid: mapUtil.isValidCoordinates([
          parseFloat(newLongitude ?? ""),
          parseFloat(newLatitude ?? ""),
        ])
          ? [parseFloat(newLongitude ?? ""), parseFloat(newLatitude ?? "")]
          : [-95, 40],
        mapZoomLevel: 12,
      };
      setMapData(newMapData);

      // Re-render map with new lease comp data (only if map is initialized)
      if (isMapReady()) {
        const coords = [
          parseFloat(newLongitude ?? ""),
          parseFloat(newLatitude ?? ""),
        ];
        if (mapUtil.isValidCoordinates(coords)) {
          renderMapPoints(coords as [number, number]);
          updateMapView(coords as [number, number], 12);
        } else {
          updateMapView([-95, 40], 2.5);
        }
      }
      
      // Reset validation states
      const lat = parseFloat(newLatitude ?? "");
      const lng = parseFloat(newLongitude ?? "");
      const latInvalid =
        isNaN(lat) ||
        lat < -90 ||
        lat > 90 ||
        !newLatitude ||
        newLatitude.trim() === "" ||
        lat === 0;
      const lngInvalid =
        isNaN(lng) ||
        lng < -180 ||
        lng > 180 ||
        !newLongitude ||
        newLongitude.trim() === "" ||
        lng === 0;
      setIsLatInvalid(latInvalid);
      setIsLngInvalid(lngInvalid);
    }
  }, [leaseComp.leaseCompId]);

  useEffect(() => {
    // Guard: ensure container ref is available before initializing map
    if (!mapAddressContainerRef.current) {
      return;
    }

    mapboxgl.accessToken = mapboxToken || "";
    mapRef.current = new mapboxgl.Map({
      container: mapAddressContainerRef.current,
      style: BAM_MAP_STYLE_SOURCES.cbreLight,
      center: mapData.mapCentroid || [-95, 40],
      zoom: mapData.mapZoomLevel || 2.5,
      attributionControl: false,
    });

    // Mark map as initialized BEFORE calling render functions
    isMapInitialized.current = true;

    const coords = [parseFloat(longitude ?? ""), parseFloat(latitude ?? "")];
    if (mapUtil.isValidCoordinates(coords)) {
      renderMapPoints(coords as [number, number]);
      updateMapView(coords as [number, number], defaultMapValues.mapZoomLevel);
    } else {
      updateMapView([-95, 40], 2.5);
    }

    const map = mapRef.current;
    mapEvent();
    mapRef.current?.resize();

    return () => {
      isMapInitialized.current = false;
      (map as any)?.off("zoomend");
      (map as any)?.off("dragend");
      mapRef.current?.remove();
    };
  }, []);

  const updateCoordinates = (value: string, field: string) => {
    const updatedValue = String(value);

    // Validate the coordinate value in real-time
    const numValue = parseFloat(updatedValue);

    if (field === "latitude") {
      // Validate latitude (treat 0 as invalid)
      const latInvalid =
        updatedValue.trim() === "" ||
        isNaN(numValue) ||
        numValue < -90 ||
        numValue > 90 ||
        numValue === 0;
      setIsLatInvalid(latInvalid);

      setCoordinates([coordinates[0], updatedValue]);
      setAddressDetails(
        (prevDetails) =>
          ({
            ...prevDetails,
            leaseCompFields: {
              ...prevDetails.leaseCompFields,
              [field]: value,
            },
          }) as typeof leaseComp,
      );
      updateAddress(value, field as keyof LeaseCompFields);
      renderMapPoints([
        coordinates[0] as unknown as number,
        updatedValue as unknown as number,
      ]);
    } else {
      // Validate longitude (treat 0 as invalid)
      const lngInvalid =
        updatedValue.trim() === "" ||
        isNaN(numValue) ||
        numValue < -180 ||
        numValue > 180 ||
        numValue === 0;
      setIsLngInvalid(lngInvalid);

      setCoordinates([updatedValue, coordinates[1]]);
      setAddressDetails(
        (prevDetails) =>
          ({
            ...prevDetails,
            leaseCompFields: {
              ...prevDetails.leaseCompFields,
              [field]: value,
            },
          }) as typeof leaseComp,
      );
      updateAddress(value, field as keyof LeaseCompFields);
      renderMapPoints([
        updatedValue as unknown as number,
        coordinates[1] as unknown as number,
      ]);
    }
  };

  const isValidGeoAddress = (addressDetails: typeof leaseComp) => {
    const {
      propertyAddress,
      propertyCity,
      propertyState,
      propertyPostalCode,
      latitude,
      longitude,
    } = addressDetails?.leaseCompFields || {};

    if (
      (!Number(latitude) || !Number(longitude)) &&
      propertyAddress &&
      propertyAddress.length > 1 &&
      ((propertyCity &&
        propertyCity.length > 1 &&
        propertyState &&
        propertyState.length > 1) ||
        (propertyPostalCode && propertyPostalCode.length >= 5))
    ) {
      const payload = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              Address: propertyAddress,
              City: propertyCity,
              StateProv: propertyState,
            },
          },
        ],
      };

      fetchCoords(payload)
        .then((coords) => {
          const areCoordinatesValid =
            !!coords?.length && mapUtil.isValidCoordinates(coords);
          if (areCoordinatesValid && mapRef.current) {
            const [longitude, latitude] = coords;
            updateAddress(`${latitude}`, "latitude");
            updateAddress(`${longitude}`, "longitude");
            return Promise.resolve().then(() => {
              setCoordinates(coords);
              setAddressDetails(
                (prevDetails) =>
                  ({
                    ...prevDetails,
                    leaseCompFields: {
                      ...prevDetails.leaseCompFields,
                      latitude: `${latitude}`,
                      longitude: `${longitude}`,
                    },
                  }) as typeof leaseComp,
              );
              if (mapRef.current) {
                mapUtil.deleteMarkers(mapRef.current);
                const updatedPoints = mapUtil.getPointsForAddressForm(
                  coords,
                  (addressDetails?.leaseCompFields || {}) as Record<
                    string,
                    unknown
                  >,
                );
                mapUtil.getMarkerConfig(updatedPoints, mapRef.current);
              }
              updateMapView(coords, 12);
              mapEvent();
            });
          } else {
            console.error("Unable to fetch coordinates.");
            updateMapView([-95, 40], 2.5);
          }
        })
        .catch((_error) => {
          console.error("Error fetching coordinates");
        });
    }
  };

  // Helper function to get field rule by key name
  const getFieldRule = (keyName: string) => {
    return fieldRules?.find((rule) => rule.keyName === keyName);
  };

  // Helper function to get additional field rules (non-address fields)
  const getAdditionalFieldRules = () => {
    const addressFields = [
      "propertyName",
      "propertyAddress",
      "propertyCity",
      "propertyState",
      "propertyPostalCode",
      "latitude",
      "longitude",
    ];
    return (
      fieldRules?.filter((rule) => !addressFields.includes(rule.keyName)) || []
    );
  };

  const hasInvalidCoordinates = useMemo(() => {
    if (!leaseComp) {
      setIsLatInvalid(false);
      setIsLngInvalid(false);
      return false;
    }

    const lat = parseFloat(latitude ?? "");
    const lng = parseFloat(longitude ?? "");

    const latInvalid =
      isNaN(lat) ||
      lat < -90 ||
      lat > 90 ||
      !latitude ||
      latitude.trim() === "" ||
      lat === 0;
    const lngInvalid =
      isNaN(lng) ||
      lng < -180 ||
      lng > 180 ||
      !longitude ||
      longitude.trim() === "" ||
      lng === 0;

    setIsLatInvalid(latInvalid);
    setIsLngInvalid(lngInvalid);

    return latInvalid || lngInvalid;
  }, [latitude, longitude]);

  const debouncedIsValidGeoAddress = useMemo(() => {
    // useMemo is needed here to prevent unnecessary re-renders
    return debounce((updatedAddressDetails) => {
      isValidGeoAddress(updatedAddressDetails);
    }, 500);
  }, []);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof LeaseCompFields,
  ) => {
    const { value } = event.target;
    // Use functional updater so that when the browser autofills multiple fields
    // simultaneously, each setState call chains off the previous queued state
    // rather than all reading the same stale closure snapshot. Without this,
    // only the last autofilled field ends up visible in the UI.
    setAddressDetails(
      (prev) =>
        ({
          ...prev,
          leaseCompFields: {
            ...prev.leaseCompFields,
            [field]: value,
          },
        }) as typeof leaseComp,
    );
    debouncedIsValidGeoAddress({
      ...addressDetails,
      leaseCompFields: {
        ...addressDetails.leaseCompFields,
        [field]: value,
      },
    } as typeof leaseComp);
  };

  const handleEditFieldLabel = (rule: LeaseCompFieldRule) => {
    setEditingFieldRule(rule);
  };

  const handleSaveFieldLabel = (newLabel: string) => {
    if (editingFieldRule && newLabel.trim()) {
      const updatedRule: LeaseCompFieldRule = {
        ...editingFieldRule,
        displayName: newLabel.trim(),
      };

      updateLeaseCompFieldRule({
        surveyId: leaseComp.surveyId,
        fieldRule: updatedRule,
      })
        .unwrap()
        .then(() => {
          setEditingFieldRule(null);
        })
        .catch((_error) => {
          console.error("Error updating field label");
        });
    }
  };

  const handleCloseEditModal = () => {
    setEditingFieldRule(null);
  };

  // Persist address fields that changed in local state but haven't been saved yet.
  // This covers browser autofill: the browser fires onChange for every filled field
  // but onBlur only fires for the single focused element, so without this effect
  // city / state / zip would update in the UI but never reach the DB.
  useEffect(() => {
    const fields = [
      "propertyAddress",
      "propertyCity",
      "propertyState",
      "propertyPostalCode",
    ] as const;

    const changedFields = fields.filter((field) => {
      const current = addressDetails?.leaseCompFields?.[field];
      const original = leaseComp?.leaseCompFields?.[field];
      return current != null && current.trim() !== "" && current !== original;
    });

    if (changedFields.length === 0) return;

    const timeoutId = setTimeout(() => {
      changedFields.forEach((field) => {
        const value = addressDetails.leaseCompFields?.[field];
        if (value && value.trim() !== "") {
          updateAddress(value, field);
        }
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    addressDetails?.leaseCompFields?.propertyAddress,
    addressDetails?.leaseCompFields?.propertyCity,
    addressDetails?.leaseCompFields?.propertyState,
    addressDetails?.leaseCompFields?.propertyPostalCode,
  ]);

  return (
    <>
      {hasInvalidCoordinates && (
        <div
          style={{
            backgroundColor: "#FFF2E5",
            borderColor: "#D5710C",
            marginTop: "-18px",
          }}
          className="px-4 py-3 border rounded-sm flex items-center gap-2"
        >
          <EmeraldIcon
            icon="warning_amber"
            type="filled"
            fontsizeInRem={1.2}
            className="text-orange-600"
            variant="icon"
          />
          <span
            className="text-warning-dark"
            style={{ fontSize: "0.875rem", fontWeight: 100 }}
          >
            Invalid or missing coordinates data. Please update this lease comp
            with valid coordinates.
          </span>
        </div>
      )}
      <div className="address-mapOverview-container grid grid-flow-row lg:grid-cols-2 gap-6 sm:grid-cols-1">
        <div className="LeaseComp-detail-address-form">
          <h2 className="mb-3 text-xl text-data">Address</h2>

          {/* Street Address - Full Width */}
          <div className="my-4">
            <EmeraldInputV2
              type="text"
              label="Street Address"
              value={normalizeValue(
                addressDetails?.leaseCompFields?.propertyAddress,
              )}
              size="small"
              className={classNames("without-label", {
                boldLabel: getFieldRule("propertyAddress")?.isActive,
              })}
              isValid={true}
              onBlur={(e) => updateAddress(e.target.value, "propertyAddress")}
              onChange={(e) => handleInputChange(e, "propertyAddress")}
            />
          </div>

          {/* City, State, Zip - 3 Column Grid */}
          <div className="gap-3 my-5 grid grid-flow-row lg:grid-cols-3 sm:grid-cols-1">
            <EmeraldInputV2
              type="text"
              label="City"
              value={normalizeValue(
                addressDetails?.leaseCompFields?.propertyCity,
              )}
              size="small"
              className={classNames("without-label", {
                boldLabel: getFieldRule("propertyCity")?.isActive,
              })}
              onBlur={(e) => updateAddress(e.target.value, "propertyCity")}
              onChange={(e) => handleInputChange(e, "propertyCity")}
            />
            <EmeraldInputV2
              type="text"
              label="State"
              value={normalizeValue(
                addressDetails?.leaseCompFields?.propertyState,
              )}
              size="small"
              className={classNames("without-label", {
                boldLabel: getFieldRule("propertyState")?.isActive,
              })}
              onBlur={(e) => updateAddress(e.target.value, "propertyState")}
              onChange={(e) => handleInputChange(e, "propertyState")}
            />
            <EmeraldInputV2
              type="text"
              label="Zip Code"
              value={normalizeValue(
                addressDetails?.leaseCompFields?.propertyPostalCode,
              )}
              size="small"
              className={classNames("without-label", {
                boldLabel: getFieldRule("propertyPostalCode")?.isActive,
              })}
              onBlur={(e) =>
                updateAddress(e.target.value, "propertyPostalCode")
              }
              onChange={(e) => handleInputChange(e, "propertyPostalCode")}
            />
          </div>

          {/* Latitude, Longitude - 2 Column Grid */}
          <div className="gap-3 my-5 grid grid-flow-row lg:grid-cols-2 sm:grid-cols-1">
            <EmeraldInputV2
              type="number"
              label="Latitude"
              value={coordinates?.[1] || ""}
              size="small"
              errorText={isLatInvalid ? "Invalid Latitude" : undefined}
              className={classNames("without-label", {
                boldLabel: getFieldRule("latitude")?.isActive,
              })}
              onChange={(e) => {
                updateCoordinates(e.target.value, "latitude");
              }}
              onBlur={(e) => {
                updateAddress(e.target.value, "latitude");
              }}
            />
            <EmeraldInputV2
              type="number"
              label="Longitude"
              value={coordinates?.[0] || ""}
              size="small"
              errorText={isLngInvalid ? "Invalid Longitude" : undefined}
              className={classNames("without-label", {
                boldLabel: getFieldRule("longitude")?.isActive,
              })}
              onChange={(e) => {
                updateCoordinates(e.target.value, "longitude");
              }}
              onBlur={(e) => {
                updateAddress(e.target.value, "longitude");
              }}
            />
          </div>
        </div>
        <div className="map-overview relative">
          <div
            id="map-address-container"
            ref={mapAddressContainerRef}
            className="h-full w-full relative"
          >
            {checkValueIsUnavailable(coordinates?.[0]) &&
              checkValueIsUnavailable(coordinates?.[1]) && <MapOverlay />}
          </div>
        </div>
      </div>

      {/* Photo Section */}
      {photoSection && (
        <div className="grid grid-flow-row gap-4 mt-6">
          <h2 className="text-xl text-data">Photo</h2>
          {photoSection}
        </div>
      )}

      {/* Property Info(Lease Details) Section - Dynamic Fields */}
      {getAdditionalFieldRules().length > 0 && (
        <div className="property-info-section">
          <h3 className="mb-3 text-xl text-data">Lease Details</h3>
          <div className="gap-3 my-5 grid grid-flow-row lg:grid-cols-2 sm:grid-cols-1">
            {getAdditionalFieldRules()
              .sort((a, b) => {
                // Sort by isActive descending (active first), then by order
                if (b.isActive !== a.isActive) {
                  return b.isActive ? 1 : -1;
                }
                return (a.order ?? 0) - (b.order ?? 0);
              })
              .map((rule) => {
                const fieldValue = leaseComp?.leaseCompFields?.[
                  rule.keyName as keyof LeaseCompFields
                ] as string | undefined;

                // If this field is being edited, show the modal instead
                if (editingFieldRule?.key === rule.key) {
                  return (
                    <EditLeaseCompFieldLabel
                      key={rule.key}
                      rule={editingFieldRule}
                      onClose={handleCloseEditModal}
                      onSave={handleSaveFieldLabel}
                    />
                  );
                }

                // Otherwise show the normal field with edit icon
                return (
                  <EditableLeaseCompField
                    key={rule.key}
                    rule={rule}
                    fieldValue={fieldValue}
                    onEdit={handleEditFieldLabel}
                    onValueChange={(value, fieldKey) =>
                      updateAddress(value, fieldKey as keyof LeaseCompFields)
                    }
                  />
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};

export default LeaseCompDetailAddressForm;
